import { ValidationResult } from './validate-url';

/**
 * Validates if a URL belongs to a specific social network.
 * Ensures that in fields for specific social networks, only URLs for that network can be added.
 *
 * Supported social networks:
 * - VK
 * - VK-Video
 * - Telegram
 * - YouTube
 * - Instagram
 *
 * @param url - The URL to validate
 * @param socialNetwork - The social network to check against
 * @returns ValidationResult - Object containing validation status and error message if invalid
 */
export function validateSocialNetworkUrl(url: string, socialNetwork: string): ValidationResult {
  if (!url) {
    return {
      isValid: false,
      errorMessage: 'URL не может быть пустым',
    };
  }

  try {
    // Create URL object to validate structure
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    const socialNetworkLower = socialNetwork.toLowerCase();

    // Get the display name for the social network (for error messages)
    let socialNetworkDisplayName: string;
    switch (socialNetworkLower) {
      case 'vk':
        socialNetworkDisplayName = 'ВКонтакте';
        break;
      case 'vk-video':
        socialNetworkDisplayName = 'ВКвидео';
        break;
      case 'telegram':
        socialNetworkDisplayName = 'Telegram';
        break;
      case 'youtube':
        socialNetworkDisplayName = 'YouTube';
        break;
      case 'instagram':
        socialNetworkDisplayName = 'Instagram';
        break;
      default:
        socialNetworkDisplayName = socialNetwork;
    }

    switch (socialNetworkLower) {
      case 'vk':
        // VK domains
        if (
          hostname === 'vk.com' ||
          hostname === 'www.vk.com' ||
          hostname.endsWith('.vk.com') ||
          hostname === 'vkontakte.ru' ||
          hostname === 'www.vkontakte.ru'
        ) {
          return {
            isValid: true,
            errorMessage: null,
          };
        }
        break;

      case 'vk-video':
        // VK Video domains
        if (hostname === 'vk.video' || hostname === 'www.vk.video' || hostname === 'vkvideo.ru' || hostname === 'www.vkvideo.ru') {
          return {
            isValid: true,
            errorMessage: null,
          };
        }
        break;

      case 'telegram':
        // Telegram domains
        if (
          hostname === 't.me' ||
          hostname === 'telegram.me' ||
          hostname === 'www.t.me' ||
          hostname === 'www.telegram.me' ||
          hostname === 'telegram.org' ||
          hostname === 'www.telegram.org'
        ) {
          return {
            isValid: true,
            errorMessage: null,
          };
        }
        break;

      case 'youtube':
        // YouTube domains
        if (hostname === 'youtube.com' || hostname === 'www.youtube.com' || hostname === 'youtu.be' || hostname === 'www.youtu.be') {
          return {
            isValid: true,
            errorMessage: null,
          };
        }
        break;

      case 'instagram':
        // Instagram domains
        if (hostname === 'instagram.com' || hostname === 'www.instagram.com' || hostname.endsWith('.instagram.com')) {
          return {
            isValid: true,
            errorMessage: null,
          };
        }
        break;

      default:
        return {
          isValid: false,
          errorMessage: `Неизвестная социальная сеть: ${socialNetwork}`,
        };
    }

    // If we get here, the URL doesn't belong to the specified social network
    return {
      isValid: false,
      errorMessage: `Эта ссылка не принадлежит ${socialNetworkDisplayName}`,
    };
  } catch (error) {
    // URL constructor will throw if the URL is invalid
    return {
      isValid: false,
      errorMessage: 'Неверный формат URL',
    };
  }
}
